import { useSectorDisplayNames } from '../hooks/useSectorDisplayNames';
import { useTranslation } from 'react-i18next';
import { t } from 'i18next';
import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useFieldBindings } from '../hooks/useFieldBindings';
import { canIssuePhysicalCommands } from '../auth/cognito';
import ProgramWizard from './ProgramWizard';
import { bindingSectors, listPrograms, programmingApiError, formatDuration, type Program } from './programmingService';

function FarmPrograms({ farmId, canCreate, sectors }: {
  farmId: string; canCreate: boolean; sectors: ReturnType<typeof bindingSectors>;
}) {
  useTranslation('programming');
  const [programs, setPrograms] = useState<Program[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refresh, setRefresh] = useState(0);
  const [wizard, setWizard] = useState(false);
  useEffect(() => {
    let active = true;
    setLoading(true); setError('');
    listPrograms(farmId).then(rows => { if (active) setPrograms(rows); })
      .catch(e => { if (active) setError(programmingApiError(e)); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [farmId, refresh]);
  return <section className="p-4 space-y-4">
    <h1 className="text-2xl font-semibold">{t("programming:text2")}</h1>
    <p>{t("programming:text3")}</p>
    <p>{t("programming:text4")}{farmId}</p>
    <button className="px-4 py-3 rounded-lg border" onClick={() => setRefresh(n => n + 1)}>{t("programming:text5")}</button>
    {canCreate && <button className="px-4 py-3 rounded-lg bg-green-700 text-white" disabled={!sectors.length || loading || !!error} onClick={() => setWizard(true)}>{t("programming:text6")}</button>}
    {loading && <p role="status">{t("programming:text7")}</p>}
    {error && <p role="alert">{error}</p>}
    {!loading && !error && programs.length === 0 && <p>{t("programming:text8")}</p>}
    {!loading && !error && programs.map(p => <article key={p.id} className="p-4 border rounded-xl">
      <h2 className="font-semibold">{p.name}</h2>
      <p>{p.status === 'DRAFT' ? t("programming:text9") : p.status} {t("programming:text10")}{p.version} · {p.steps.length} {t("programming:text11")}</p>
      <p>{p.timezone}</p>
      {p.schedule && <div data-testid="persisted-schedule">
        <p>{t("programming:text47")} · {p.schedule.type === 'ONCE' ? t("programming:text52") : p.schedule.type === 'DAILY' ? t("programming:text53") : p.schedule.type === 'WEEKLY' ? t("programming:text54") : t("programming:text55")}</p>
        <p>{p.schedule.local_date || p.schedule.start_local_date} {p.schedule.local_time}</p>
        {p.schedule.interval_days && <p>{t("programming:text56")} {p.schedule.interval_days} {t("programming:text57")}</p>}
        {p.schedule.weekdays && <p>{p.schedule.weekdays.map(day => ({
          MONDAY: t("programming:text15"), TUESDAY: t("programming:text16"), WEDNESDAY: t("programming:text17"),
          THURSDAY: t("programming:text18"), FRIDAY: t("programming:text19"), SATURDAY: t("programming:text20"), SUNDAY: t("programming:text21"),
        }[day] || day)).join(' · ')}</p>}
      </div>}
      <ol>{p.steps.map((s, i) => <li key={s.binding_id}>
        {i + 1}. {sectors.find(b => b.id === s.binding_id)?.name || s.binding_id} · {formatDuration(s.duration_seconds / 60)}
        {i < p.steps.length - 1 && <> · {t("programming:text36")}: {t("programming:seconds", { count: s.overlap_to_next_seconds || 0 })}</>}
      </li>)}</ol>
    </article>)}
    {wizard && canCreate && <ProgramWizard sectors={sectors} farmId={farmId} onClose={() => setWizard(false)} onSaved={() => { setWizard(false); setRefresh(n => n + 1); }} />}
  </section>;
}
export default function ProgrammingPage() {
  useTranslation('programming');
  const { user } = useAuth();
  const bindings = useFieldBindings();
  const { customNames } = useSectorDisplayNames(bindings.farmId ?? '');
  const sectors = useMemo(() => bindingSectors(bindings.parcelBindings, customNames), [bindings.parcelBindings, customNames]);
  if (user?.org_id !== 'org-cgm' || user.role?.trim().toLowerCase() === 'superadmin') return <p>{t("programming:text12")}</p>;
  if (!bindings.loaded) return <p role="status">{t("programming:text13")}</p>;
  if (bindings.error || !bindings.farmId) return <p role="alert">{t("programming:text14")}</p>;
  const canCreate = canIssuePhysicalCommands(user.role);
  return <FarmPrograms key={`${user.org_id}:${user.email}:${bindings.farmId}`} farmId={bindings.farmId} sectors={sectors} canCreate={canCreate} />;
}
